import type {
  CashBookController_cashBookPage_v1Query,
  CashBookEntryResource,
} from '@repo/api-client';

/**
 * Turning one cash-book row into words, and nothing else.
 *
 * Kept out of the screen for the reason `expense-form.ts` is: these are the
 * decisions a reader has to be able to check, and three of the four are places
 * where the obvious rendering is wrong in a way that renders perfectly.
 */

/** `'CREDIT' | 'DEBIT'`, from the resource rather than restated. */
type Direction = CashBookEntryResource['direction'];

/**
 * The kinds of movement the route may be narrowed to, from the query
 * parameter's own enum.
 *
 * **Taken from the operation's query type**, so the filter offers exactly what
 * the route accepts. A value this system does not write is refused `400` naming
 * the field rather than answered with an empty page, so an option this list
 * invented would be a control that always fails — and one it dropped would be a
 * filter the API supports and the screen hides.
 *
 * This is deliberately *not* the same set as {@link labelForReferenceType}
 * handles. `referenceType` on a **row** is documented as free text — "a cause is
 * named by the feature that adds one" — so the column must render a kind this
 * build has never seen, while the filter must offer only what the query
 * declares. Conflating the two would either hide rows or offer a filter that
 * 400s.
 *
 * Keyed by the query's enum with `satisfies`, so a cause added upstream fails
 * `typecheck` until it has a label and a place in the filter, rather than being
 * silently absent from the picker.
 */
type NarrowableReferenceType = NonNullable<
  CashBookController_cashBookPage_v1Query['referenceType']
>;

const NARROWABLE_LABELS = {
  PRODUCT_SALE: 'Product sale',
  SALE_REFUND: 'Refund',
  TICKET_SALE: 'Ticket',
  // Not "Offline ticket": what a reader wants to know is that a person entered
  // it rather than a gate scanning it, which is the question this kind exists
  // to answer — "which admissions were entered by hand, and by whom".
  OFFLINE_TICKET_SALE: 'Ticket entered by hand',
  // Money taken on a stand-alone billing machine and imported from its export.
  // Never a till sale: it carries no terminal (FR-1139).
  MACHINE_SALE: 'Machine sale',
} satisfies Record<NarrowableReferenceType, string>;

export const NARROWABLE_REFERENCE_TYPES = Object.keys(
  NARROWABLE_LABELS,
) as readonly NarrowableReferenceType[];

const REFERENCE_TYPE_LABELS: Record<string, string> = NARROWABLE_LABELS;

/**
 * What caused a movement, in words.
 *
 * An unmapped kind renders **as itself** rather than as "Unknown" or "Other".
 * The column is free text by contract, so a build meeting a cause added after it
 * shipped is the ordinary case and not a fault — and the code is the only thing
 * the row knows about what moved the money, so replacing it with a placeholder
 * throws away the answer. Same rule as an unmapped error code, which is quoted
 * so staff can read it down the phone.
 */
export const labelForReferenceType = (referenceType: string): string =>
  REFERENCE_TYPE_LABELS[referenceType] ?? (referenceType === '' ? '—' : referenceType);

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
};

/**
 * How the money moved, where the movement knows.
 *
 * **Null is a recorded fact rather than missing data** (R-407): Ticketing
 * captures no payment mode at all, so every ticket's entry carries null and
 * "must not be read as cash". Said in words for that reason — an empty cell in a
 * column whose other rows say "Cash" is read as cash by proximity, which is the
 * one interpretation the contract rules out.
 *
 * `labelForPaymentMode` in `routes/sales` is not reused: it takes
 * `SaleResource['paymentMode']`, which is the three-value enum and non-null, and
 * widening it to accept `string | null` would let a null reach a lookup that has
 * no answer for one.
 */
export const labelForEntryPaymentMode = (paymentMode: string | null): string => {
  if (paymentMode === null) return 'Not recorded';

  return PAYMENT_MODE_LABELS[paymentMode] ?? paymentMode;
};

/**
 * Which way the money went, as a short label and the sentence behind it.
 *
 * **The sign lives here and never on the amount.** `amountPaise` is "a
 * non-negative magnitude on every row, reversals included", so a table that
 * rendered a debit as `−₹450.00` would be inventing a sign the contract
 * deliberately keeps in its own field — and a reader adding the column up would
 * get a number the API never published. The totals in the envelope are what the
 * period nets to.
 */
export function describeDirection(direction: Direction): {
  readonly label: string;
  readonly description: string;
} {
  return direction === 'CREDIT'
    ? { label: 'In', description: 'Money taken in' }
    : { label: 'Out', description: 'Money handed back' };
}
